// ============================================================
// Protected Route Guard
// ============================================================
// Protects routes from unauthenticated access and optionally
// checks for specific RBAC permissions.

import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { useAuth, usePermissions } from '@/hooks';
import useAuthStore from '@/store/authStore';
import { PATHS } from '@/router/paths';
import { ROLES } from '@/constants/roles';

export function ProtectedRoute({ requiredPermission, allowedRoles }) {
  const { isAuthenticated, isInitialized, user } = useAuth();
  const isSessionExpired = useAuthStore((state) => state.isSessionExpired);
  const { can, canAny } = usePermissions();
  const location = useLocation();

  // Wait for auth to initialize before making decisions
  if (!isInitialized) return null;

  // 1. Check Authentication (If session is expired, hold route so modal displays over current page)
  if (!isAuthenticated && !isSessionExpired) {
    return <Navigate to={PATHS.LOGIN} state={{ from: location }} replace />;
  }

  // 2. Check Role Restrictions if specified
  if (allowedRoles) {
    const userRole = String(user?.role || '').toUpperCase();
    const roleList = (Array.isArray(allowedRoles) ? allowedRoles : [allowedRoles]).map((r) => String(r).toUpperCase());
    if (!roleList.includes(userRole)) {
      return <Navigate to={PATHS.UNAUTHORIZED} replace />;
    }
  }

  // 3. Check Authorization (if permission is required for this route)
  if (requiredPermission) {
    const hasPermission = Array.isArray(requiredPermission)
      ? canAny(requiredPermission)
      : can(requiredPermission);
      
    if (!hasPermission) {
      if (user?.role === ROLES.SUPER_ADMIN && location.pathname === PATHS.DASHBOARD) {
        return <Navigate to={PATHS.SUPER_ADMIN_DASHBOARD} replace />;
      }
      return <Navigate to={PATHS.UNAUTHORIZED} replace />;
    }
  }

  // 4. Render Route
  return <Outlet />;
}
